import { Box } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { RanaHeading, RanaTable } from "@nens/ranalib";

import { BackButton } from "@/components/back-button";
import { TableEmptyHistory } from "@/components/table-empty-history";
import { getProjectTabQueryParam } from "@/features/project";
import { Profile } from "@/lib/profile";
import { convertFileIdToQueryParams } from "@/utils/convert-file-id-to-query-params";
import { disabledFileHistoryProps } from "@/utils/disabled-file-history-props";
import { getPrettyTime } from "@/utils/get-pretty-time";

import { useProjectFileHistory } from "../hooks/use-project-file-history";

import { ProjectConfirmRevertVersionModal } from "./project-file-confirm-revert-version-modal";

interface ProjectFileHistoryProps {
  path: string;
  fileName: string | undefined;
  fileRef?: string;
  back: () => void;
}

export function ProjectFileHistory({ path, fileName, fileRef, back }: ProjectFileHistoryProps) {
  const intl = useIntl();
  const {
    openModal,
    closeModal,
    getActions,
    fileHistories,
    fileId,
    searchString,
    isLoading,
    setSearchString,
  } = useProjectFileHistory({ path, fileName });

  return (
    <Box mb={2}>
      {openModal?.type === "revert-to-version" && (
        <ProjectConfirmRevertVersionModal
          fileId={fileId}
          fileRef={openModal.item.ref}
          onClose={closeModal}
        />
      )}
      <Box gap={2} alignItems="center">
        <BackButton back={back}>
          {fileName ? (
            <FormattedMessage id="button.file" defaultMessage="File" />
          ) : (
            <FormattedMessage id="button.files" defaultMessage="Files" />
          )}
        </BackButton>
        <RanaHeading type="h3" my={2}>
          <FormattedMessage id="button.history" defaultMessage="History" />
        </RanaHeading>
      </Box>
      <RanaTable
        emptyElement={<TableEmptyHistory isSearching={!!searchString} />}
        columnDefinitions={[
          {
            width: "table_cell_sm",
            title: intl.formatMessage({ id: "column.timestamp", defaultMessage: "Timestamp" }),
          },
          {
            width: "table_cell_l",
            title: intl.formatMessage({ id: "column.event", defaultMessage: "Event" }),
          },
          {
            width: "table_cell_xxxs",
            title: intl.formatMessage({ id: "column.user", defaultMessage: "User" }),
          },
          {
            width: "table_cell_xxxs",
            title: "", // For current version
          },
        ]}
        tableDataIds={fileHistories.map((item) => item.ref)}
        isSkeleton={isLoading}
        hasRowActions
      >
        <RanaTable.Filters
          search={{
            placeholder: intl.formatMessage({
              id: "search.versions",
              defaultMessage: "Find versions",
            }),
            searchString,
            setSearchString,
          }}
        />
        <RanaTable.Container>
          <RanaTable.Header />
          <RanaTable.Body>
            {fileHistories.map((item, idx) => {
              const { ref, createdAt, message, committedBy } = item;
              let [, historyItemFileId] = message.match(/[`']([^`']+)[`']/) ?? [];
              const prettyCreatedAt = getPrettyTime(createdAt);
              // If message includes directory but the fileId does not end with /, add it because it is very likely a directory
              if (message.includes("directory") && !historyItemFileId.endsWith("/")) {
                historyItemFileId += "/";
              }

              return (
                <RanaTable.Row
                  isSelected={ref === fileRef}
                  key={ref}
                  rowId={ref}
                  queryParams={`${getProjectTabQueryParam("files")}&${convertFileIdToQueryParams(
                    historyItemFileId + "",
                    ref
                  )}`}
                  rowActions={getActions(item, idx === 0)}
                  {...disabledFileHistoryProps(intl, message)}
                >
                  <RanaTable.Cell>{prettyCreatedAt}</RanaTable.Cell>
                  <RanaTable.Cell>{message}</RanaTable.Cell>
                  <RanaTable.Cell>
                    <Profile inTable user={committedBy} />
                  </RanaTable.Cell>
                  <RanaTable.Cell
                    display="flex"
                    justifyContent="flex-end"
                    fontStyle="italic"
                    color="brand.clear_creek"
                    fontSize="sm"
                  >
                    {ref === fileRef && (
                      <FormattedMessage
                        id="label.current_version"
                        defaultMessage="Current version"
                      />
                    )}
                  </RanaTable.Cell>
                </RanaTable.Row>
              );
            })}
          </RanaTable.Body>
        </RanaTable.Container>
      </RanaTable>
    </Box>
  );
}
